'use client';

import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import { IconButton } from '@terra/ui-design-system';
import { Add01Icon } from '@terra/ui-icons';
import {
  useTabGroups,
  useMainContentTabs,
  useLayoutStore,
  type Tab,
} from '../../store/layoutStore';
import { useDetachedWindows } from '../../hooks/useDetachedWindows';
import { useTargetType } from '../../store/projectStore';
import { isAppTarget } from '../../utils/target-scope.utils';
import { wheelDeltaYToHorizontalScrollPixels } from '../../utils/wheelDeltaYToHorizontalScrollPixels';
import { TabItem } from './grouped-tab-bar/TabItem';
import { TabGroupHeader } from './grouped-tab-bar/TabGroupHeader';
import { TabContextMenu } from './grouped-tab-bar/TabContextMenu';
import { TabSearchMenu } from './grouped-tab-bar/TabSearchMenu';
import { useGroupedTabDragAndDrop } from './grouped-tab-bar/useGroupedTabDragAndDrop';
import { useTabSelection } from './grouped-tab-bar/useTabSelection';
import { getDecorativeBorderColor } from './grouped-tab-bar/tabGroupDecorativeColor';

const NO_DRAG = { WebkitAppRegion: 'no-drag' } as React.CSSProperties;

export function GroupedTabBar() {
  const t = useTranslations('layout');
  const targetType = useTargetType();
  const {
    tabs: allTabs,
    setActiveTab,
    closeTab,
    togglePinTab,
    reorderTabs,
    openInNewTab,
  } = useMainContentTabs();
  const detachedTabIds = useLayoutStore((state) => state.detachedTabIds);
  const detachedTabIdSet = useMemo(
    () => new Set(detachedTabIds),
    [detachedTabIds],
  );
  const tabs = allTabs.filter((tab) => !detachedTabIdSet.has(tab.id));
  const { detachTabs } = useDetachedWindows();
  const canDetachTabs = !!window.electronAPI?.detachedWindows;
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [contextMenuTab, setContextMenuTab] = useState<string | null>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [isNewGroupEdit, setIsNewGroupEdit] = useState(false);
  const tabRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const {
    tabGroups,
    createTabGroup,
    deleteTabGroup,
    updateTabGroup,
    addTabToGroup,
    removeTabFromGroup,
    toggleGroupCollapse,
    reorderTabGroups,
    reorderTabsInGroup,
    getTabsInGroup,
    getUngroupedTabs,
  } = useTabGroups();
  const {
    selectedTabIds,
    setSelectedTabIds,
    setSelectionAnchorId,
    handleSelect,
  } = useTabSelection({
    tabs,
    allTabs,
    detachedTabIds: detachedTabIdSet,
    tabGroups,
    getTabsInGroup,
    getUngroupedTabs,
    setActiveTab,
  });
  const {
    draggedTabIndex,
    dragOverIndex,
    draggedGroupIndex,
    dragOverGroupIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleGroupDragStart,
    handleGroupDragEnd,
    handleGroupDragOver,
    handleGroupDrop,
  } = useGroupedTabDragAndDrop({
    tabs,
    allTabs,
    tabGroups,
    reorderTabs,
    addTabToGroup,
    removeTabFromGroup,
    reorderTabsInGroup,
    updateTabGroup,
    reorderTabGroups,
  });
  const activeTab = tabs.find((tab) => tab.isActive);
  const activeTabId = activeTab?.id;

  useEffect(() => {
    if (activeTabId) {
      const tabElement = tabRefs.current.get(activeTabId);

      tabElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [activeTabId]);
  const [pendingScrollTabId, setPendingScrollTabId] = useState<string | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!pendingScrollTabId) return;
    const stillExists = tabs.some((tab) => tab.id === pendingScrollTabId);

    if (!stillExists) {
      setPendingScrollTabId(null);

      return;
    }

    const tabElement = tabRefs.current.get(pendingScrollTabId);

    if (!tabElement) return;
    tabElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
    setPendingScrollTabId(null);
  }, [pendingScrollTabId, tabGroups, tabs]);
  const handleCreateTab = useCallback(() => {
    openInNewTab({
      id: 'application-overview',
      title: isAppTarget(targetType) ? t('home') : t('targetOverview'),
      type: 'application',
    });
  }, [openInNewTab, t, targetType]);
  const handleSearchSelectTab = useCallback(
    (tabId: string) => {
      const tab = tabs.find((candidate) => candidate.id === tabId);

      if (tab?.groupId) {
        const group = tabGroups.find(
          (candidate) => candidate.id === tab.groupId,
        );

        if (group?.isCollapsed) {
          updateTabGroup(group.id, { isCollapsed: false });
        }
      }

      setSelectedTabIds(new Set());
      setSelectionAnchorId(tabId);
      setActiveTab(tabId);
      setPendingScrollTabId(tabId);
    },
    [
      tabs,
      tabGroups,
      updateTabGroup,
      setActiveTab,
      setSelectedTabIds,
      setSelectionAnchorId,
    ],
  );
  const resolveGroupTargetIds = useCallback((): string[] => {
    const existingIds = new Set(tabs.map((tab) => tab.id));

    if (
      contextMenuTab &&
      selectedTabIds.has(contextMenuTab) &&
      selectedTabIds.size > 1
    ) {
      return [...selectedTabIds].filter((id) => existingIds.has(id));
    }

    return contextMenuTab && existingIds.has(contextMenuTab)
      ? [contextMenuTab]
      : [];
  }, [contextMenuTab, selectedTabIds, tabs]);
  const handleContextMenu = useCallback(
    (e: React.MouseEvent, tabId: string) => {
      e.preventDefault();
      if (!selectedTabIds.has(tabId)) {
        setSelectedTabIds(new Set([tabId]));
        setSelectionAnchorId(tabId);
      }

      const tabElement = tabRefs.current.get(tabId);
      const rect = tabElement?.getBoundingClientRect();

      setContextMenuAnchor(
        rect
          ? {
              x: rect.left,
              y: rect.top,
              width: rect.width,
              height: rect.height,
            }
          : {
              x: e.clientX,
              y: e.clientY,
              width: 0,
              height: 0,
            },
      );
      setContextMenuTab(tabId);
      setContextMenuOpen(true);
    },
    [selectedTabIds, setSelectedTabIds, setSelectionAnchorId],
  );
  const handleCloseContextMenu = useCallback(() => {
    setContextMenuOpen(false);
    setContextMenuTab(null);
    setContextMenuAnchor(null);
  }, []);
  const handleCreateGroupTab = useCallback(() => {
    const tabIds = resolveGroupTargetIds();

    if (tabIds.length === 0) return;
    const groupId = createTabGroup({
      name: t('defaultGroupName'),
      type: 'custom',
      color: 'blue',
      isCollapsed: false,
      tabIds,
    });

    setSelectedTabIds(new Set());
    handleCloseContextMenu();
    setIsNewGroupEdit(true);
    setEditingGroupId(groupId);
  }, [
    resolveGroupTargetIds,
    createTabGroup,
    handleCloseContextMenu,
    t,
    setSelectedTabIds,
  ]);
  const handlePinSelectedTabs = useCallback(() => {
    const tabIds = resolveGroupTargetIds();
    const tabsById = new Map(tabs.map((tab) => [tab.id, tab]));
    const shouldUnpin = tabIds.every((id) => tabsById.get(id)?.isPinned);

    tabIds.forEach((tabId) => {
      const tab = tabsById.get(tabId);

      if (!tab) return;
      const needsToggle = shouldUnpin ? tab.isPinned : !tab.isPinned;

      if (!needsToggle) return;
      togglePinTab(tabId);
    });
    setSelectedTabIds(new Set());
  }, [resolveGroupTargetIds, tabs, togglePinTab, setSelectedTabIds]);
  const handleCloseSelectedTabs = useCallback(() => {
    resolveGroupTargetIds().forEach((tabId) => closeTab(tabId));
    setSelectedTabIds(new Set());
  }, [resolveGroupTargetIds, closeTab, setSelectedTabIds]);
  const handleDetachSelectedTabs = useCallback(() => {
    void detachTabs(resolveGroupTargetIds());
    setSelectedTabIds(new Set());
  }, [resolveGroupTargetIds, detachTabs, setSelectedTabIds]);
  const handleAddToExistingGroup = useCallback(
    (groupId: string) => {
      const tabsById = new Map(tabs.map((tab) => [tab.id, tab]));

      resolveGroupTargetIds()
        .filter((tabId) => tabsById.get(tabId)?.groupId !== groupId)
        .forEach((tabId) => addTabToGroup(tabId, groupId));
      setSelectedTabIds(new Set());
    },
    [resolveGroupTargetIds, addTabToGroup, tabs, setSelectedTabIds],
  );
  const handleRemoveFromGroup = useCallback(() => {
    resolveGroupTargetIds().forEach((tabId) => removeTabFromGroup(tabId));
    setSelectedTabIds(new Set());
  }, [resolveGroupTargetIds, removeTabFromGroup, setSelectedTabIds]);
  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      const group = tabGroups.find((g) => g.id === groupId);

      if (!group) return;
      group.tabIds.forEach((tabId) => closeTab(tabId));
      deleteTabGroup(groupId);
      if (editingGroupId === groupId) {
        setEditingGroupId(null);
        setIsNewGroupEdit(false);
      }
    },
    [tabGroups, closeTab, deleteTabGroup, editingGroupId],
  );
  const handleUngroup = useCallback(
    (groupId: string) => {
      const group = tabGroups.find((g) => g.id === groupId);

      if (!group) return;
      group.tabIds.forEach((tabId) => removeTabFromGroup(tabId));
      deleteTabGroup(groupId);
      if (editingGroupId === groupId) {
        setEditingGroupId(null);
        setIsNewGroupEdit(false);
      }
    },
    [tabGroups, removeTabFromGroup, deleteTabGroup, editingGroupId],
  );
  const renderTabItem = (tab: Tab, isGrouped = false) => {
    const tabIndex = tabs.findIndex((t) => t.id === tab.id);

    return (
      <TabItem
        key={tab.id}
        tab={tab}
        isHovered={hoveredTab === tab.id}
        isDragging={draggedTabIndex === tabIndex}
        isDragOver={dragOverIndex === tabIndex}
        isSelected={selectedTabIds.has(tab.id)}
        isGrouped={isGrouped}
        onHover={setHoveredTab}
        onSelect={handleSelect}
        onClose={closeTab}
        onPin={togglePinTab}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart({ index: tabIndex, tab })}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver(tabIndex)}
        onDrop={handleDrop(tabIndex)}
        tabsCount={tabs.length}
        tabRef={(el) => {
          if (el) tabRefs.current.set(tab.id, el);
          else tabRefs.current.delete(tab.id);
        }}
      />
    );
  };

  const pinnedTabs = tabs.filter((tab) => tab.isPinned && !tab.groupId);
  const ungroupedTabs = getUngroupedTabs(tabs).filter((tab) => !tab.isPinned);
  const contextMenuTabData = contextMenuTab
    ? (tabs.find((tab) => tab.id === contextMenuTab) ?? null)
    : null;
  const contextMenuTargetIds = resolveGroupTargetIds();
  const tabsById = useMemo(
    () => new Map(tabs.map((tab) => [tab.id, tab])),
    [tabs],
  );
  const shouldUnpinSelected =
    contextMenuTargetIds.length > 0 &&
    contextMenuTargetIds.every((id) => tabsById.get(id)?.isPinned);
  const canRemoveFromGroup = contextMenuTargetIds.some(
    (id) => tabsById.get(id)?.groupId,
  );

  return (
    <Box
      h="full"
      w="full"
      display="flex"
      alignItems="center"
      gap="1"
      minW="0"
      style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}
    >
      <Box
        flex="1"
        minW="0"
        h="full"
        display="flex"
        alignItems="center"
        gap="1"
        overflowX="auto"
        overflowY="hidden"
        css={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        onWheel={(e) => {
          const el = e.currentTarget;
          const delta = wheelDeltaYToHorizontalScrollPixels(e.nativeEvent, el);

          if (delta !== 0) el.scrollLeft += delta;
        }}
      >
        {pinnedTabs.map((tab) => renderTabItem(tab))}

        {pinnedTabs.length > 0 &&
          (ungroupedTabs.length > 0 || tabGroups.length > 0) && (
            <Box w="1px" bg="border.default" h="full" flexShrink={0} />
          )}

        {tabGroups.map((group, groupIndex) => {
          const groupTabs = getTabsInGroup(group.id, tabs);

          if (groupTabs.length === 0) return null;
          const isCollapsed = group.isCollapsed;
          const isDragOverGroup = dragOverGroupIndex === groupIndex;

          return (
            <Box
              key={group.id}
              data-tab-group-shell=""
              display="flex"
              alignItems="center"
              gap="1.5"
              flexShrink={1}
              h="8"
              p={isCollapsed ? '0' : '1'}
              borderRadius="md"
              borderWidth={isCollapsed ? '0' : '1px'}
              borderStyle="solid"
              bg={isCollapsed ? undefined : 'bg.neutral.light'}
              opacity={isDragOverGroup && draggedTabIndex !== null ? 0.85 : 1}
              style={
                isCollapsed
                  ? NO_DRAG
                  : {
                      ...NO_DRAG,
                      borderColor: getDecorativeBorderColor(group.color),
                    }
              }
              onDragOver={handleGroupDragOver(groupIndex)}
              onDrop={handleGroupDrop({ groupId: group.id, index: groupIndex })}
            >
              <TabGroupHeader
                group={group}
                isEditingOpen={editingGroupId === group.id}
                startWithEmptyName={
                  isNewGroupEdit && editingGroupId === group.id
                }
                onEditingOpenChange={(open) => {
                  if (open) {
                    setEditingGroupId(group.id);
                  } else {
                    setEditingGroupId(null);
                    setIsNewGroupEdit(false);
                  }
                }}
                onToggleCollapse={() => toggleGroupCollapse(group.id)}
                onRenameGroup={(name) => updateTabGroup(group.id, { name })}
                onChangeColor={(color) => updateTabGroup(group.id, { color })}
                onUngroup={() => handleUngroup(group.id)}
                onDeleteGroup={() => handleDeleteGroup(group.id)}
                isDragging={draggedGroupIndex === groupIndex}
                isDragOver={isDragOverGroup && draggedGroupIndex !== null}
                onDragStart={handleGroupDragStart(groupIndex)}
                onDragEnd={handleGroupDragEnd}
                onDragOver={handleGroupDragOver(groupIndex)}
                onDrop={handleGroupDrop({
                  groupId: group.id,
                  index: groupIndex,
                })}
              />
              {!isCollapsed && groupTabs.map((tab) => renderTabItem(tab, true))}
            </Box>
          );
        })}

        {ungroupedTabs.map((tab) => renderTabItem(tab))}

        <Box
          position="sticky"
          right={0}
          flexShrink={0}
          display="flex"
          alignItems="center"
          pl="1"
          bg="bg.light"
          zIndex={1}
          style={NO_DRAG}
        >
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={t('newTab')}
            onClick={handleCreateTab}
            color="fg.muted"
            style={NO_DRAG}
          >
            <Add01Icon size={16} strokeWidth={1.75} />
          </IconButton>
        </Box>
      </Box>

      <Box flexShrink={0} display="flex" alignItems="center" style={NO_DRAG}>
        <TabSearchMenu tabs={tabs} onSelectTab={handleSearchSelectTab} />
      </Box>

      <TabContextMenu
        open={contextMenuOpen}
        anchorRect={contextMenuAnchor}
        contextMenuTabData={contextMenuTabData}
        tabGroups={tabGroups}
        selectedCount={contextMenuTargetIds.length}
        shouldUnpin={shouldUnpinSelected}
        canPinTabs
        canDetachTabs={canDetachTabs}
        canRemoveFromGroup={canRemoveFromGroup}
        onClose={handleCloseContextMenu}
        onPinTabs={handlePinSelectedTabs}
        onDetachTab={handleDetachSelectedTabs}
        onCreateGroup={handleCreateGroupTab}
        onAddToExistingGroup={handleAddToExistingGroup}
        onRemoveFromGroup={handleRemoveFromGroup}
        onCloseTabs={handleCloseSelectedTabs}
      />
    </Box>
  );
}
